import { Badge } from '@/components/ui/badge'
import { X } from 'lucide-react'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Info } from 'lucide-react'
import { evaluationOptions, WinType, winTypes } from '@db8/shared'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

import { cn } from '@/lib/utils'

export const WinConditionFormField = ({ field, form, disabled = false }) => {
  return (
    <FormItem>
      <div className="flex items-center gap-2 mb-2">
        <FormLabel className="font-semibold">Win Condition</FormLabel>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info size="16px" className="cursor-pointer text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent className="w-72 text-sm text-left">
            <ul className="space-y-2 list-disc list-inside">
              <li>
                <strong>Basic Poll:</strong> Spectators vote for who they think won. The majority
                vote determines the winner.
              </li>
              <li>
                <strong>Multi Criteria Poll:</strong> Spectators rate debaters based on multiple
                criteria. The average scores determine the winner.
              </li>
              <li>
                <strong>Manual:</strong> The room owner manually selects the winner after the debate
                concludes.
              </li>
              <li>
                <strong>AI:</strong> An AI automatically evaluates the debate and selects a winner,
                providing a reasoning summary.
              </li>
              <li>
                <strong>Ballot:</strong> Assigned judges vote on the winner using a structured
                ballot system.
              </li>
            </ul>
          </TooltipContent>
        </Tooltip>
      </div>

      <Select
        onValueChange={field.onChange}
        defaultValue={form.getValues().winType || WinType.BALLOT}
        disabled={disabled}
      >
        <FormControl className="bg-secondary">
          <SelectTrigger>
            <SelectValue placeholder="Select a debate format." />
          </SelectTrigger>
        </FormControl>
        <SelectContent className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 w-full">
          {winTypes
            .filter((type) => form.watch('aiEnabled') || type.type !== WinType.AI)
            .map((format) => (
              <SelectItem
                key={format.type}
                value={format.type}
                className="flex w-full items-center justify-between"
              >
                {format.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      <FormMessage />

      {form.watch('winType') === WinType.EVALUATIONPOLL && (
        <FormField
          control={form.control}
          name="evaluationCriteria"
          render={() => {
            const selected = form.watch('evaluationCriteria') || []

            const toggleCriterion = (criterion: string) => {
              const updated = selected.includes(criterion)
                ? selected.filter((c) => c !== criterion)
                : selected.length < 5
                  ? [...selected, criterion]
                  : selected
              form.setValue('evaluationCriteria', updated, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }

            return (
              <FormItem className="mt-6">
                <FormLabel className="font-semibold">Evaluation Criteria (up to 5)</FormLabel>
                <div className="flex flex-wrap justify-center gap-2 mt-2">
                  {evaluationOptions.map((criterion) => {
                    const isSelected = selected.includes(criterion)

                    return (
                      <Badge
                        key={criterion}
                        variant={isSelected ? 'outline' : 'secondary'}
                        className={cn(
                          'cursor-pointer',
                          isSelected &&
                            'border-green-500 dark:border-transparent dark:bg-foreground  dark:text-background pr-1.5',
                        )}
                        onClick={() => toggleCriterion(criterion)}
                      >
                        <span className="mr-1">{criterion}</span>
                        {isSelected && (
                          <X
                            size={12}
                            className="ml-1"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleCriterion(criterion)
                            }}
                          />
                        )}
                      </Badge>
                    )
                  })}
                </div>
                {selected.length >= 5 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Maximum of 5 criteria allowed.
                  </p>
                )}
              </FormItem>
            )
          }}
        />
      )}
    </FormItem>
  )
}
